import * as pc from 'playcanvas';
import { matCache } from '../utils/MaterialCache';

export class BulletPool {
  private app: pc.Application;
  private pool: pc.Entity[] = [];
  private active: Map<string, { entity: pc.Entity; vx: number; vz: number; life: number; maxLife: number }> = new Map();

  constructor(app: pc.Application) {
    this.app = app;
  }

  private getBulletMat(weaponTier: number, variant = 0): pc.StandardMaterial {
    let r: number, g: number, b: number;
    if (variant === 1) {
      [r, g, b] = [1.0, 0.3, 0.0];
    } else if (variant === 2) {
      [r, g, b] = [0.3, 0.75, 1.0];
    } else {
      const colors: [number, number, number][] = [
        [0.9, 0.7, 0.1],  // 0 single  - yellow
        [0.9, 0.7, 0.1],  // 1 double  - yellow
        [0.9, 0.4, 0.1],  // 2 shotgun - orange
        [0.1, 0.9, 1.0],  // 3 sniper  - cyan
        [1.0, 0.4, 0.0],  // 4 mortar  - red/orange
        [0.1, 0.9, 1.0],  // 5 laser   - cyan
      ];
      [r, g, b] = colors[weaponTier] ?? colors[0];
    }
    const col = new pc.Color(r, g, b);
    return matCache.get({
      diffuse: col,
      emissive: col,
      emissiveIntensity: variant > 0 ? 3.5 : 2.5,
      blendType: pc.BLEND_ADDITIVE,
      depthWrite: false,
    });
  }

  private getFromPool(): pc.Entity {
    if (this.pool.length > 0) return this.pool.pop()!;
    const e = new pc.Entity('bullet');
    e.addComponent('render', { type: 'sphere' });
    e.render!.castShadows = false;
    e.render!.receiveShadows = false;
    this.app.root.addChild(e);
    return e;
  }

  spawn(id: string, x: number, y: number, z: number, vx: number, vz: number, weaponTier: number, speedMult = 1.0, variant = 0): void {
    if (this.active.has(id)) return;

    const e = this.getFromPool();
    const baseSize = weaponTier === 3 ? 0.22 : weaponTier === 4 ? 0.3 : 0.15;
    const size = variant > 0 ? baseSize * 1.5 : baseSize;  // powerup bullets are larger
    e.setLocalScale(size, size, size);
    e.setPosition(x, y, z);
    e.enabled = true;

    e.render!.meshInstances[0].material = this.getBulletMat(weaponTier, variant);

    const baseSpeed = [20, 20, 16, 35, 14, 50][weaponTier] ?? 20;
    const range     = [18, 18, 10, 30, 14, 25][weaponTier] ?? 18;
    const speed     = baseSpeed * speedMult;

    this.active.set(id, {
      entity: e,
      vx: vx * speed,
      vz: vz * speed,
      life: 0,
      maxLife: range / speed,
    });
  }

  // Spawn a remote bullet. Client predicts position using vx/vz and auto-expires via ttl.
  spawnServer(id: string, x: number, z: number, vx: number, vz: number, weaponTier: number, ttl: number, variant = 0): void {
    if (this.active.has(id)) return;

    const e = this.getFromPool();
    const baseSize = weaponTier === 3 ? 0.22 : weaponTier === 4 ? 0.3 : 0.15;
    const size = variant > 0 ? baseSize * 1.5 : baseSize;
    e.setLocalScale(size, size, size);
    e.setPosition(x, 0.5, z);
    e.enabled = true;
    e.render!.meshInstances[0].material = this.getBulletMat(weaponTier, variant);

    this.active.set(id, { entity: e, vx, vz, life: 0, maxLife: ttl });
  }

  remove(id: string): pc.Vec3 | null {
    const b = this.active.get(id);
    if (!b) return null;
    const pos = b.entity.getPosition().clone();
    b.entity.enabled = false;
    this.pool.push(b.entity);
    this.active.delete(id);
    return pos;
  }

  forEachActive(cb: (bx: number, bz: number) => void): void {
    this.active.forEach((b) => {
      if (b.entity.enabled) {
        const pos = b.entity.getPosition();
        cb(pos.x, pos.z);
      }
    });
  }

  destroyAll(): void {
    this.active.forEach((b) => {
      b.entity.enabled = false;
      this.pool.push(b.entity);
    });
    this.active.clear();
  }

  update(dt: number): string[] {
    const expired: string[] = [];
    this.active.forEach((b, id) => {
      b.life += dt;
      if (b.life >= b.maxLife) {
        b.entity.enabled = false;
        this.pool.push(b.entity);
        expired.push(id);
        return;
      }
      const pos = b.entity.getPosition();
      b.entity.setPosition(
        pos.x + b.vx * dt,
        pos.y,
        pos.z + b.vz * dt,
      );
    });
    expired.forEach(id => this.active.delete(id));
    return expired;
  }
}
